import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, map, Observable, of } from 'rxjs';

import {
  BondType,
  BondTypeApiItem,
  DeleteBondTypeResult,
  UpsertBondTypeRequest,
} from './bond-type.types';
import { apiUrl } from '../../../auth/api-url';

@Injectable({
  providedIn: 'root',
})
export class BondTypeService {
  private readonly endpoint = apiUrl('bondtype');

  constructor(private readonly http: HttpClient) {}

  getBondTypes(): Observable<BondType[]> {
    return this.http.get<unknown>(this.endpoint).pipe(
      map((response) => this.mapRows(response, (item) => this.normalizeBondType(item))),
    );
  }

  createBondType(payload: UpsertBondTypeRequest): Observable<void> {
    return this.http.post<void>(this.endpoint, payload);
  }

  updateBondType(id: number, payload: UpsertBondTypeRequest): Observable<void> {
    return this.http.put<void>(`${this.endpoint}/${id}`, payload);
  }

  deleteBondType(id: number): Observable<DeleteBondTypeResult> {
    return this.http.delete<void>(`${this.endpoint}/${id}`).pipe(
      map((): DeleteBondTypeResult => ({ kind: 'success' })),
      catchError(() => of<DeleteBondTypeResult>({
        kind: 'error',
        message: 'Unable to delete bond type at this time. Please try again.',
      })),
    );
  }

  private normalizeBondType(item: BondTypeApiItem): BondType {
    return {
      id: this.toNumber(item.typeId),
      name: this.toText(item.name),
      description: this.toText(item.description),
    };
  }

  private mapRows<T>(response: unknown, mapper: (item: BondTypeApiItem) => T): T[] {
    return this.extractRows(response).map(mapper);
  }

  private extractRows(response: unknown): BondTypeApiItem[] {
    if (Array.isArray(response)) {
      return response as BondTypeApiItem[];
    }

    if (response && typeof response === 'object') {
      const record = response as Record<string, unknown>;
      const data = record['data'];
      const items = record['items'];

      if (Array.isArray(data)) {
        return data as BondTypeApiItem[];
      }

      if (Array.isArray(items)) {
        return items as BondTypeApiItem[];
      }
    }

    return [];
  }

  private toText(value: unknown): string {
    return typeof value === 'string' ? value : '';
  }

  private toNumber(value: unknown): number {
    return typeof value === 'number' && Number.isFinite(value) ? value : 0;
  }
}
